/**
 * Image compression utility for client-side file size reduction
 * Supports JPEG and WebP formats with target size constraints
 * Note: PNG support temporarily disabled - PNG images will be converted to JPEG
 */

export interface CompressionOptions {
  targetSize: number; // Target size in bytes (1MB = 1048576, 5MB = 5242880)
  maxWidth?: number; // Maximum width in pixels (default: 4096)
  maxHeight?: number; // Maximum height in pixels (default: 4096)
  initialQuality?: number; // Initial quality for compression (0-1, default: 0.9)
  // format?: 'jpeg' | 'webp' | 'png'; // Output format (default: 'jpeg')
  format?: 'jpeg' | 'webp'; // Output format (default: 'jpeg') - PNG temporarily disabled
  onProgress?: (progress: number) => void; // Progress callback (0-100)
  resizeEnabled?: boolean; // Enable/disable image resizing (default: true)
  minSize?: number; // Minimum size in bytes (optional) - prevent over-compression
}

export interface CompressionResult {
  blob: Blob;
  file: File;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
  finalDimensions: { width: number; height: number };
  finalQuality: number;
}

/**
 * Compresses an image file to meet the target size constraint
 * Uses a combination of dimension reduction and quality adjustment
 */
/**
 * Resize an image to fit within maximum dimensions while maintaining aspect ratio
 * @param img - The image element to resize
 * @param maxWidth - Maximum width in pixels
 * @param maxHeight - Maximum height in pixels
 * @returns Canvas with resized image or null if resizing is not needed
 */
export async function resizeImage(
  img: HTMLImageElement,
  maxWidth: number,
  maxHeight: number
): Promise<{ canvas: HTMLCanvasElement; resized: boolean } | null> {
  const { width, height } = calculateDimensions(
    img.width,
    img.height,
    maxWidth,
    maxHeight
  );

  // If no resizing needed, return null
  if (width === img.width && height === img.height) {
    return null;
  }

  const { canvas, ctx } = getCanvas();
  canvas.width = width;
  canvas.height = height;

  // Draw resized image
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  return { canvas, resized: true };
}

export async function compressImage(
  file: File,
  options: CompressionOptions
): Promise<CompressionResult> {
  const {
    targetSize,
    maxWidth = 4096,
    maxHeight = 4096,
    initialQuality = 0.9,
    format = 'jpeg',
    onProgress,
    resizeEnabled = false,
    minSize
  } = options;


  // Validate input
  if (!file.type.startsWith('image/')) {
    throw new Error('Input file must be an image');
  }

  const originalSize = file.size;
  onProgress?.(0);

  // Load image into canvas
  const img = await loadImage(file);
  onProgress?.(10);

  let width = img.width;
  let height = img.height;
  let resizedImg: HTMLImageElement = img;

  // Apply resizing if enabled
  if (resizeEnabled) {
    const resizeResult = await resizeImage(img, maxWidth, maxHeight);
    if (resizeResult) {
      // Create new image from resized canvas
      const resizedBlob = await new Promise<Blob>((resolve, reject) => {
        resizeResult.canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Failed to create resized blob'));
          },
          'image/jpeg',
          1.0 // Use maximum quality for intermediate step
        );
      });
      resizedImg = await loadImage(new File([resizedBlob], 'resized.jpg', { type: 'image/jpeg' }));
      width = resizeResult.canvas.width;
      height = resizeResult.canvas.height;
    }
  }

  // If the original file is already smaller than target, return it
  if (originalSize <= targetSize) {
    onProgress?.(100);
    return {
      blob: file,
      file,
      originalSize,
      compressedSize: originalSize,
      compressionRatio: 1,
      finalDimensions: { width: img.width, height: img.height },
      finalQuality: 1
    };
  }

  // 初期品質を画像サイズに基づいて推定（より高い品質から開始）
  const sizeRatio = targetSize / originalSize;
  // サイズ比率に基づいて、より積極的な初期品質を設定
  // initialQualityが指定されている場合はそれを使用
  const estimatedQuality = initialQuality || Math.min(0.95, Math.max(0.5, Math.sqrt(sizeRatio) * 1.1));
  
  // First, try compression with quality adjustment only
  let result = await findOptimalQuality(
    resizedImg,
    width,
    height,
    targetSize,
    format,
    estimatedQuality, // 推定品質を使用
    (progress) => onProgress?.(10 + progress * 0.4),
    minSize
  );

  // If quality adjustment alone isn't enough, progressively reduce dimensions
  if (result.blob.size > targetSize && resizeEnabled) {
    result = await findOptimalDimensionsAndQuality(
      resizedImg,
      width,
      height,
      targetSize,
      format,
      (progress) => onProgress?.(50 + progress * 0.5)
    );
  }

  onProgress?.(100);

  // Create a new File object from the compressed blob
  const compressedFile = new File(
    [result.blob],
    file.name.replace(/\.[^/.]+$/, `.${format}`),
    { type: `image/${format}` }
  );


  return {
    blob: result.blob,
    file: compressedFile,
    originalSize,
    compressedSize: result.blob.size,
    compressionRatio: result.blob.size / originalSize,
    finalDimensions: { width: result.width, height: result.height },
    finalQuality: result.quality
  };
}

/**
 * Loads an image file and returns an HTMLImageElement
 */
async function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Calculates dimensions maintaining aspect ratio within max bounds
 */
function calculateDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;

  // Scale down if needed
  if (width > maxWidth || height > maxHeight) {
    const aspectRatio = width / height;

    if (width > maxWidth) {
      width = maxWidth;
      height = width / aspectRatio;
    }

    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
  }

  return {
    width: Math.round(width),
    height: Math.round(height)
  };
}

// Canvas要素をキャッシュして再利用
let cachedCanvas: HTMLCanvasElement | null = null;
let cachedContext: CanvasRenderingContext2D | null = null;

/**
 * Gets or creates a reusable canvas element
 */
function getCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  if (!cachedCanvas || !cachedContext) {
    cachedCanvas = document.createElement('canvas');
    cachedContext = cachedCanvas.getContext('2d');
    if (!cachedContext) {
      throw new Error('Failed to get canvas context');
    }
  }
  return { canvas: cachedCanvas, ctx: cachedContext };
}

/**
 * Renders an image to canvas and converts to blob
 */
async function renderToBlob(
  img: HTMLImageElement,
  width: number,
  height: number,
  quality: number,
  format: 'jpeg' | 'webp' | 'png'
): Promise<Blob> {
  const { canvas, ctx } = getCanvas();

  canvas.width = width;
  canvas.height = height;

  // Use white background for JPEG to handle transparency
  if (format === 'jpeg') {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
  } 
  // else if (format === 'png') {
  //   // Clear canvas for PNG transparency
  //   ctx.clearRect(0, 0, width, height);
  // }

  // Draw image with smoothing
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      `image/${format}`,
      // format === 'png' ? undefined : quality
      quality // Always use quality parameter
    );
  });
}

/**
 * Uses binary search to find optimal quality setting for target size
 */
async function findOptimalQuality(
  img: HTMLImageElement,
  width: number,
  height: number,
  targetSize: number,
  // format: 'jpeg' | 'webp' | 'png',
  format: 'jpeg' | 'webp', // PNG temporarily disabled
  initialQuality: number,
  onProgress?: (progress: number) => void,
  minSize?: number
): Promise<{ blob: Blob; quality: number; width: number; height: number }> {
  // // PNGの場合は品質調整ができないため、単一のblobを返す
  // if (format === 'png') {
  //   const blob = await renderToBlob(img, width, height, 1, format);
  //   return { blob, quality: 1, width, height };
  // }

  let minQuality = 0.1;
  let maxQuality = initialQuality;
  let bestResult: { blob: Blob; quality: number } | null = null;
  const tolerance = 0.02; // より細かい調整のため許容範囲を縮小
  const maxIterations = 15; // イテレーション数を増やして精度向上
  let iterations = 0;
  
  // 目標サイズの90%を最小許容サイズとする（デフォルト）
  // 5MBの場合は90%（4.5MB）を最小許容サイズとする
  let minAcceptableSize = targetSize * 0.9;  // 全て90%で統一
  
  // 最小サイズが指定されている場合、それを考慮
  if (minSize && minSize > minAcceptableSize) {
    minAcceptableSize = minSize;
  }

  while (maxQuality - minQuality > tolerance && iterations < maxIterations) {
    const quality = (minQuality + maxQuality) / 2;
    const blob = await renderToBlob(img, width, height, quality, format);

    onProgress?.(iterations / maxIterations * 100);

    if (blob.size <= targetSize) {
      // 目標サイズ以下かつ最小許容サイズ以上の場合、最良の結果として保存
      if (blob.size >= minAcceptableSize) {
        bestResult = { blob, quality };
        // 目標サイズの90%～100%の範囲に収まったら、より大きいサイズを探す
        minQuality = quality;
      } else {
        // サイズが小さすぎる場合は、品質を上げる
        minQuality = quality;
      }
    } else {
      // 目標サイズを超えている場合
      maxQuality = quality;
    }

    iterations++;
  }

  // If no valid result found, try to find the best compromise
  if (!bestResult) {
    // 最終的な品質で再度レンダリングして、目標サイズに最も近いものを選択
    const finalQuality = maxQuality;
    const blob = await renderToBlob(img, width, height, finalQuality, format);
    
    // 目標サイズを超えている場合は許容しない（特に5MBの場合）
    if (blob.size <= targetSize) {
      bestResult = { blob, quality: finalQuality };
    } else {
      // 目標サイズを超えている場合は、より低い品質で再試行
      let searchQuality = finalQuality;
      for (let i = 0; i < 5; i++) {
        searchQuality *= 0.9; // 10%ずつ品質を下げる
        const testBlob = await renderToBlob(img, width, height, searchQuality, format);
        if (testBlob.size <= targetSize) {
          bestResult = { blob: testBlob, quality: searchQuality };
          break;
        }
      }
      
      // それでも大きすぎる場合は、最小品質を使用
      if (!bestResult || bestResult.blob.size > targetSize) {
        const minBlob = await renderToBlob(img, width, height, 0.1, format);
        bestResult = { blob: minBlob, quality: 0.1 };
      }
    }
  }

  return {
    blob: bestResult.blob,
    quality: bestResult.quality,
    width,
    height
  };
}

/**
 * Progressively reduces dimensions and adjusts quality to meet target size
 */
async function findOptimalDimensionsAndQuality(
  img: HTMLImageElement,
  initialWidth: number,
  initialHeight: number,
  targetSize: number,
  // format: 'jpeg' | 'webp' | 'png',
  format: 'jpeg' | 'webp', // PNG temporarily disabled
  onProgress?: (progress: number) => void
): Promise<{ blob: Blob; quality: number; width: number; height: number }> {
  const scaleFactor = 0.9; // より細かい調整のため縮小率を緩やかに
  let width = initialWidth;
  let height = initialHeight;
  let bestResult: { blob: Blob; quality: number; width: number; height: number } | null = null;
  const minDimension = 480; // Minimum dimension to maintain reasonable quality
  let iterations = 0;
  const maxIterations = 5; // イテレーション数を削減

  while (
    width > minDimension &&
    height > minDimension &&
    iterations < maxIterations
  ) {
    // Try to find optimal quality at current dimensions
    const result = await findOptimalQuality(
      img,
      width,
      height,
      targetSize,
      format,
      0.95,
      (progress) => onProgress?.(iterations / maxIterations * 100)
    );

    if (result.blob.size <= targetSize) {
      bestResult = result;
      // 目標サイズの90%以上に達していれば、これ以上縮小しない
      if (result.blob.size >= targetSize * 0.9) {
        break;
      }
    }

    // Reduce dimensions for next iteration
    width = Math.round(width * scaleFactor);
    height = Math.round(height * scaleFactor);
    iterations++;
  }

  // If still no valid result, use minimum dimensions with lowest quality
  if (!bestResult) {
    const finalWidth = Math.max(width, minDimension);
    const finalHeight = Math.max(height, minDimension);
    const blob = await renderToBlob(img, finalWidth, finalHeight, 0.1, format);
    bestResult = {
      blob,
      quality: 0.1,
      width: finalWidth,
      height: finalHeight
    };
  }

  return bestResult;
}

/**
 * Helper function to convert bytes to human-readable format
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Predefined compression presets
 */
export const CompressionPresets = {
  ONE_MB: 1048576, // 1 MB in bytes
  FIVE_MB: 5242880, // 5 MB in bytes
} as const;